<template>
  <div class="timeline">
    <div v-for="day in groupedAudit" :key="day.key" class="timeline-day">
      <div class="timeline-day-head">
        <span class="timeline-dot"></span>
        <span>{{ day.label }}</span>
      </div>
      <div class="timeline-items">
        <div v-for="(l, i) in day.logs" :key="i" class="timeline-card">
          <div class="d-flex align-items-center flex-wrap" style="gap: 0.5rem">
            <b-badge :variant="actionVariant(l.action)">
              {{ actionLabel(l.action) }}
            </b-badge>
            <span>
              <strong>{{ roleLabel(l.roleKey) }}</strong>
              <span class="text-muted small">· {{ scopeText(l) }}</span>
            </span>
            <span class="ml-auto text-muted small">{{ formatTime(l.at) }}</span>
          </div>
          <div class="small mt-1">
            <template v-if="showTarget && l.targetUid">
              เป้าหมาย
              <b-link @click="$emit('target-click', l.targetUid)">
                {{ userLabel(l.targetUid) }}
              </b-link>
              ·
            </template>
            ผู้ดำเนินการ <strong>{{ l.byUid ? userLabel(l.byUid) : "ระบบ" }}</strong>
          </div>
          <div v-if="l.reason" class="reason-box mt-2">
            <span class="text-muted">เหตุผล: </span>{{ l.reason }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import { ROLE_LABELS_TH } from "@/constants/roles";

// Shared day-grouped audit timeline used by UserDetailModal and the AuditLog view.
export default {
  name: "AuditTimeline",
  props: {
    logs: {
      type: Array,
      default: () => [],
    },
    // Show (and link) the target UID — on for the clinic-wide log, off inside a
    // single user's detail modal where the target is implicit.
    showTarget: {
      type: Boolean,
      default: false,
    },
    // Optional lookup maps so the timeline can display names instead of raw IDs.
    // userMap: { [uid]: displayName }, branchMap: { [branchId]: displayName }
    userMap: {
      type: Object,
      default: () => ({}),
    },
    branchMap: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    // Bucket the (already ordered) logs into day groups for the timeline rail.
    groupedAudit() {
      const groups = [];
      const byKey = new Map();
      this.logs.forEach(l => {
        const key = l.at ? moment(l.at).format("YYYY-MM-DD") : "unknown";
        if (!byKey.has(key)) {
          const g = { key, label: this.dayLabel(l.at), logs: [] };
          byKey.set(key, g);
          groups.push(g);
        }
        byKey.get(key).logs.push(l);
      });
      return groups;
    },
  },
  methods: {
    roleLabel(rk) {
      return ROLE_LABELS_TH[rk] || rk;
    },
    formatTime(dt) {
      return dt ? moment(dt).format("HH:mm") : "-";
    },
    // Friendly day header: today / yesterday / explicit date.
    dayLabel(dt) {
      if (!dt) return "ไม่ทราบวันที่";
      const m = moment(dt);
      const today = moment().startOf("day");
      const diff = m.clone().startOf("day").diff(today, "days");
      if (diff === 0) return `วันนี้ · ${m.format("D MMM YYYY")}`;
      if (diff === -1) return `เมื่อวาน · ${m.format("D MMM YYYY")}`;
      return m.format("D MMM YYYY");
    },
    actionLabel(action) {
      const map = {
        ASSIGN: "+ มอบ",
        REMOVE: "− ถอด",
        SCOPE: "⊙ ขอบเขต",
        SWAP: "⇄ โอนย้าย",
      };
      return map[action] || action;
    },
    actionVariant(action) {
      const map = {
        ASSIGN: "success",
        REMOVE: "danger",
        SCOPE: "primary",
        SWAP: "warning",
      };
      return map[action] || "secondary";
    },
    scopeText(l) {
      if (l.scopeType === "clinic_wide") return "ระดับคลินิก";
      if (l.scopeType === "branch") {
        return `สาขา ${this.branchLabel(l.branchId)}`;
      }
      return l.scopeType || "";
    },
    userLabel(uid) {
      return this.userMap[uid] || `UID ${uid}`;
    },
    branchLabel(id) {
      return this.branchMap[id] || `#${id}`;
    },
  },
};
</script>

<style scoped>
.timeline {
  position: relative;
  padding-left: 0.5rem;
}

.timeline-day {
  position: relative;
  margin-bottom: 1.25rem;
}

.timeline-day::before {
  content: "";
  position: absolute;
  left: 6px;
  top: 18px;
  bottom: -1rem;
  width: 2px;
  background: #e9ecef;
}

.timeline-day:last-child::before {
  display: none;
}

.timeline-day-head {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.timeline-dot {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid #2f5b8c;
  background: #fff8df;
  z-index: 1;
}

.timeline-items {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-left: 1.5rem;
}

.timeline-card {
  border: 1px solid #e9ecef;
  border-radius: 0.5rem;
  padding: 0.625rem 0.75rem;
  background: #fff;
}

.reason-box {
  border: 1px dashed #ced4da;
  border-radius: 0.4rem;
  padding: 0.375rem 0.625rem;
  font-size: 0.85rem;
  background: #fbfbfb;
}
</style>
